import { useState } from 'react';
import { Search, MapPin, Navigation, Link2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { regionsAPI } from '@/api/regions';
import type { RegionMatch, NearestRegionRequest } from '@/types';

/**
 * Nearest Region Search Component
 * Allows users to find the nearest region using various input types:
 * - Text search (Arabic or English)
 * - GPS coordinates
 * - Google Maps URL
 */
export function NearestRegionSearch() {
  const [searchType, setSearchType] = useState<'text' | 'coordinates' | 'url'>('text');
  const [query, setQuery] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RegionMatch | null>(null);
  const [processingTime, setProcessingTime] = useState<number | null>(null);

  // Get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('الموقع الجغرافي غير مدعوم في متصفحك');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toString());
        setLongitude(position.coords.longitude.toString());
        setLoading(false);
        setSearchType('coordinates');
        toast.success('تم الحصول على موقعك الحالي');
      },
      (error) => {
        const errorMsg = `خطأ في الحصول على الموقع: ${error.message}`;
        setError(errorMsg);
        toast.error(errorMsg);
        setLoading(false);
      }
    );
  };

  // Handle search
  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setProcessingTime(null);

    try {
      const requestData: NearestRegionRequest = {};

      if (searchType === 'text' && query) {
        requestData.query = query;
      } else if (searchType === 'coordinates' && latitude && longitude) {
        requestData.latitude = parseFloat(latitude);
        requestData.longitude = parseFloat(longitude);
      } else if (searchType === 'url' && googleMapsUrl) {
        requestData.googleMapsUrl = googleMapsUrl;
      } else {
        throw new Error('الرجاء إدخال معايير بحث صحيحة');
      }

      const response = await regionsAPI.findNearest(requestData);

      if (response.data.data.region) {
        setResult(response.data.data.region);
        setProcessingTime(response.data.data.processingTimeMs);
        toast.success('تم العثور على أقرب منطقة بنجاح');
      } else {
        setError('لم يتم العثور على منطقة مطابقة');
        toast.error('لم يتم العثور على منطقة مطابقة');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error?.message || err.message || 'فشل البحث';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Clear results
  const handleClear = () => {
    setQuery('');
    setLatitude('');
    setLongitude('');
    setGoogleMapsUrl('');
    setResult(null);
    setError(null);
    setProcessingTime(null);
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Main Search Card */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <Search className="h-8 w-8 text-primary" />
          <h2 className="text-2xl font-bold text-white">البحث عن أقرب منطقة</h2>
        </div>

        {/* Search Type Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            طريقة البحث
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={() => setSearchType('text')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                searchType === 'text'
                  ? 'bg-primary text-dark shadow-glow'
                  : 'bg-dark-lighter text-gray-300 border border-gray-700 hover:border-primary'
              }`}
            >
              <Search className="h-5 w-5" />
              بحث نصي
            </button>
            <button
              onClick={() => setSearchType('coordinates')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                searchType === 'coordinates'
                  ? 'bg-primary text-dark shadow-glow'
                  : 'bg-dark-lighter text-gray-300 border border-gray-700 hover:border-primary'
              }`}
            >
              <MapPin className="h-5 w-5" />
              إحداثيات GPS
            </button>
            <button
              onClick={() => setSearchType('url')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                searchType === 'url'
                  ? 'bg-primary text-dark shadow-glow'
                  : 'bg-dark-lighter text-gray-300 border border-gray-700 hover:border-primary'
              }`}
            >
              <Link2 className="h-5 w-5" />
              رابط خرائط Google
            </button>
          </div>
        </div>

        {/* Search Inputs */}
        <div className="mb-6">
          {searchType === 'text' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                اسم المنطقة (عربي أو إنجليزي)
              </label>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="مثال: الاندلس أو Jabriya"
                className="w-full px-4 py-3 bg-dark-lighter border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          )}

          {searchType === 'coordinates' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    خط العرض (Latitude)
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    placeholder="29.3199"
                    className="w-full px-4 py-3 bg-dark-lighter border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    خط الطول (Longitude)
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    placeholder="48.0255"
                    className="w-full px-4 py-3 bg-dark-lighter border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    dir="ltr"
                  />
                </div>
              </div>
              <button
                onClick={getCurrentLocation}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-success text-dark font-semibold rounded-lg hover:bg-success/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Navigation className="h-5 w-5" />
                استخدام موقعي الحالي
              </button>
            </div>
          )}

          {searchType === 'url' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                رابط خرائط Google
              </label>
              <input
                type="text"
                value={googleMapsUrl}
                onChange={(e) => setGoogleMapsUrl(e.target.value)}
                placeholder="https://maps.app.goo.gl/xyz أو https://www.google.com/maps/place/..."
                className="w-full px-4 py-3 bg-dark-lighter border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                dir="ltr"
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={handleSearch}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-dark font-semibold rounded-lg hover:bg-primary/90 transition-all shadow-glow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="h-5 w-5 border-2 border-dark border-t-transparent rounded-full animate-spin" />
                جاري البحث...
              </>
            ) : (
              <>
                <Search className="h-5 w-5" />
                البحث عن أقرب منطقة
              </>
            )}
          </button>
          <button
            onClick={handleClear}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-dark-lighter text-gray-300 font-semibold rounded-lg border border-gray-700 hover:border-primary transition-all"
          >
            <X className="h-5 w-5" />
            مسح
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-danger/20 border border-danger/30 rounded-lg">
            <p className="text-danger font-medium">⚠️ {error}</p>
          </div>
        )}
      </div>

      {/* Results Card */}
      {result && (
        <div className="card bg-gradient-to-br from-success/20 to-success/10 border-success/30">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/30 rounded-lg">
                <MapPin className="h-6 w-6 text-success" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-success">✓ تم العثور على أقرب منطقة</h3>
                {processingTime && (
                  <p className="text-sm text-gray-400">
                    تم الإيجاد في {processingTime} ميلي ثانية
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Region Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-dark/30 rounded-lg border border-gray-700">
                <p className="text-sm text-gray-400 mb-1">الاسم العربي</p>
                <p className="text-lg font-bold text-white">{result.nameAr}</p>
              </div>
              <div className="p-4 bg-dark/30 rounded-lg border border-gray-700">
                <p className="text-sm text-gray-400 mb-1">English Name</p>
                <p className="text-lg font-bold text-white" dir="ltr">{result.nameEn}</p>
              </div>
            </div>

            {/* Governorate */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-dark/30 rounded-lg border border-gray-700">
                <p className="text-sm text-gray-400 mb-1">المحافظة</p>
                <p className="text-md font-semibold text-white">{result.governorateAr}</p>
              </div>
              <div className="p-4 bg-dark/30 rounded-lg border border-gray-700">
                <p className="text-sm text-gray-400 mb-1">Governorate</p>
                <p className="text-md font-semibold text-white" dir="ltr">{result.governorateEn}</p>
              </div>
            </div>

            {/* Match Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-700">
              <div>
                <p className="text-sm text-gray-400 mb-2">نوع المطابقة</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  result.matchType === 'exact' ? 'bg-success/30 text-success border border-success/50' :
                  result.matchType === 'fuzzy' ? 'bg-warning/30 text-warning border border-warning/50' :
                  result.matchType === 'proximity' ? 'bg-info/30 text-info border border-info/50' :
                  'bg-primary/30 text-primary border border-primary/50'
                }`}>
                  {result.matchType === 'exact' ? 'مطابقة تامة' :
                   result.matchType === 'fuzzy' ? 'مطابقة تقريبية' :
                   result.matchType === 'proximity' ? 'بالقرب' : result.matchType}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-2">مستوى الثقة</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-dark-lighter rounded-full h-2 border border-gray-700">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${result.confidence * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-primary">
                    {Math.round(result.confidence * 100)}%
                  </span>
                </div>
              </div>
              {result.distance !== undefined && result.distance !== null && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">المسافة</p>
                  <p className="text-md font-bold text-white">
                    {result.distance.toFixed(2)} كم
                  </p>
                </div>
              )}
            </div>

            {/* Coordinates */}
            {result.latitude && result.longitude && (
              <div className="pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400 mb-2">الإحداثيات</p>
                <p className="text-sm text-white font-mono mb-3" dir="ltr">
                  {result.latitude}, {result.longitude}
                </p>
                <a
                  href={`https://www.google.com/maps/place/${result.latitude},${result.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-info/20 text-info rounded-lg border border-info/30 hover:bg-info/30 transition-all text-sm font-medium"
                >
                  <MapPin className="h-4 w-4" />
                  عرض على خرائط Google
                </a>
              </div>
            )}

            {/* Additional Info */}
            {(result.matchedField || result.matchMethod) && (
              <div className="pt-4 border-t border-gray-700 text-sm text-gray-400">
                {result.matchedField && (
                  <p>الحقل المطابق: <span className="font-medium text-gray-300">{result.matchedField}</span></p>
                )}
                {result.matchMethod && (
                  <p>طريقة المطابقة: <span className="font-medium text-gray-300">{result.matchMethod}</span></p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Usage Examples */}
      <div className="card bg-info/10 border-info/30">
        <h3 className="text-lg font-bold mb-4 text-info flex items-center gap-2">
          💡 أمثلة على الاستخدام
        </h3>
        <div className="space-y-2 text-sm text-gray-300">
          <p><strong className="text-primary">بحث نصي:</strong> جرب "الاندلس" أو "Jabriya" أو "الجهراء"</p>
          <p><strong className="text-primary">إحداثيات GPS:</strong> استخدم إحداثيات GPS أو انقر "استخدام موقعي الحالي"</p>
          <p><strong className="text-primary">رابط خرائط Google:</strong> الصق أي رابط خرائط Google (بما في ذلك روابط goo.gl المختصرة)</p>
        </div>
      </div>
    </div>
  );
}
